/**
 * Which kind of device an audit row came from: the frontend mirror of the API's
 * surface registry (`lib/privacypal-api/server/utils/client-surfaces.js`).
 *
 * This is a different question from the AI provider. `platform` on an audit row
 * answers "which AI did this go to" and is rendered from `aiProviders.ts`;
 * `client` answers "which PrivacyPal surface protected it", and the form factor
 * below is the rollup a person actually asks for: a phone or a computer.
 *
 * Keep the ids and the form-factor mapping in step with the API registry. The
 * API is authoritative; if the two disagree, a row will render under one label
 * and be counted under another.
 *
 * `unknown` is not an error state. It is every row written before the dimension
 * existed, plus any client not yet listed. It is shown as "Unattributed" rather
 * than hidden, because the surface buckets have to sum to the totals printed
 * above them.
 */

export type FormFactor = 'mobile' | 'desktop' | 'browser' | 'server' | 'unknown';

/** Series order. Keep it stable: the stacked charts depend on it. */
export const FORM_FACTORS: FormFactor[] = ['desktop', 'mobile', 'browser', 'server', 'unknown'];

export interface FormFactorMeta {
  key: FormFactor;
  label: string;
  /** Series color per theme, from the brand kit's accent roles. */
  color: { light: string; dark: string };
}

/**
 * Three accent roles plus two greys, which keeps a stacked bar inside the brand
 * kit's rule of at most three palette colors in one chart: Signal Teal for the
 * desktop app, Burnt Tangerine for mobile, Forest Sage for browser, and the two
 * non-device buckets in Espresso and a neutral so they read as "not a person's
 * device" rather than as a fourth product.
 */
export const FORM_FACTOR_META: Record<FormFactor, FormFactorMeta> = {
  desktop: {
    key: 'desktop',
    label: 'Desktop',
    color: { light: '#028391', dark: '#4FC8D4' },
  },
  mobile: {
    key: 'mobile',
    label: 'Mobile',
    color: { light: '#D97A35', dark: '#F0914D' },
  },
  browser: {
    key: 'browser',
    label: 'Browser',
    color: { light: '#4DA394', dark: '#6FC9B6' },
  },
  server: {
    key: 'server',
    label: 'Server',
    color: { light: '#503D2D', dark: '#CFC0AE' },
  },
  unknown: {
    key: 'unknown',
    label: 'Unattributed',
    color: { light: '#8793A8', dark: '#5C6C82' },
  },
};

interface ClientSurface {
  clientId: string;
  name: string;
  formFactor: FormFactor;
}

const CLIENT_SURFACES: ClientSurface[] = [
  { clientId: 'privacypal-ios', name: 'iPhone', formFactor: 'mobile' },
  { clientId: 'privacypal-android', name: 'Android', formFactor: 'mobile' },
  { clientId: 'privacypal-family-ios', name: 'Family (iPhone)', formFactor: 'mobile' },
  { clientId: 'privacypal-family-android', name: 'Family (Android)', formFactor: 'mobile' },
  { clientId: 'privacypal-proxy', name: 'Desktop app', formFactor: 'desktop' },
  { clientId: 'privacypal-family-desktop', name: 'Family desktop', formFactor: 'desktop' },
  { clientId: 'privacypal-browser-extension', name: 'Browser extension', formFactor: 'browser' },
  { clientId: 'privacypal-user-portal', name: 'Portal', formFactor: 'server' },
  { clientId: 'privacypal-family-portal', name: 'Parent HQ', formFactor: 'server' },
  { clientId: 'privacypal-family-api', name: 'Family API', formFactor: 'server' },
  { clientId: 'privacypal-sdk-demo', name: 'SDK', formFactor: 'server' },
  { clientId: 'privacypal-private-mcp', name: 'Private MCP', formFactor: 'server' },
];

const BY_CLIENT_ID = new Map(CLIENT_SURFACES.map((surface) => [surface.clientId, surface]));

/**
 * The form factor for a `client` value. Never throws and never returns null, so
 * a caller can use it directly as a bucket key.
 */
export function formFactorOf(client: string | null | undefined): FormFactor {
  if (typeof client !== 'string') return 'unknown';
  const raw = client.trim().toLowerCase();
  if (!raw) return 'unknown';
  return BY_CLIENT_ID.get(raw)?.formFactor ?? 'unknown';
}

/**
 * A short label for a row badge: the specific device where we know it
 * ("iPhone", "Desktop app"), the form factor where we only know that much.
 */
export function clientDisplayName(client: string | null | undefined): string {
  if (typeof client !== 'string' || !client.trim()) {
    return FORM_FACTOR_META.unknown.label;
  }
  const raw = client.trim().toLowerCase();
  return BY_CLIENT_ID.get(raw)?.name ?? FORM_FACTOR_META[formFactorOf(raw)].label;
}

/** The badge colour for a row, by form factor. */
export function surfaceColor(
  client: string | null | undefined,
  isDark: boolean,
): string {
  const meta = FORM_FACTOR_META[formFactorOf(client)];
  return isDark ? meta.color.dark : meta.color.light;
}

export function formFactorLabel(key: FormFactor): string {
  return FORM_FACTOR_META[key].label;
}
